import { create } from 'zustand';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/shared/lib/supabase';

/**
 * Two-factor sign-in state.
 *
 * Supabase signs a person in at aal1 (password, magic link, Google) even
 * when they have a verified TOTP factor; it is the app's job to ask for the
 * code and raise the session to aal2. Until then the session is "pending":
 * the guards treat the person as not signed in and the sign-in page shows
 * the code step.
 *
 * - `idle`: no session, nothing to check.
 * - `checking`: a new person's session is being read; the app waits.
 * - `required`: aal1 with a verified factor: the code step must be passed.
 * - `satisfied`: aal2, or no verified factor.
 */
export type MfaStatus = 'idle' | 'checking' | 'required' | 'satisfied';

interface MfaState {
  status: MfaStatus;
  /** Whose session the status describes. */
  userId: string | null;
  /** The access token last checked, so repeated calls cost nothing. */
  checkedToken: string | null;
}

export const useMfaStore = create<MfaState>(() => ({
  status: 'idle',
  userId: null,
  checkedToken: null,
}));

let inflight: { token: string; promise: Promise<MfaStatus> } | null = null;
/** The token of the newest check; an older check's answer is dropped. */
let latestToken: string | null = null;

/** Whether the session's user has a verified factor (read without a call). */
function hasVerifiedFactor(session: Session): boolean {
  return (session.user.factors ?? []).some((f) => f.status === 'verified');
}

/**
 * Reads the session's assurance level and records whether the code step is
 * needed. Deferred a tick: called from onAuthStateChange, an SDK call inside
 * the callback would wait on the lock the callback is holding.
 */
export function checkMfaAssurance(session: Session | null): Promise<MfaStatus> {
  if (!session) {
    inflight = null;
    latestToken = null;
    useMfaStore.setState({ status: 'idle', userId: null, checkedToken: null });
    return Promise.resolve('idle');
  }
  const token = session.access_token;
  const state = useMfaStore.getState();
  if (state.checkedToken === token && (state.status === 'required' || state.status === 'satisfied')) {
    return Promise.resolve(state.status);
  }
  if (inflight?.token === token) return inflight.promise;
  latestToken = token;

  // A refreshed token for the same person keeps the known status while it is
  // re-read; the app would otherwise blank out on every token refresh.
  if (state.userId !== session.user.id || state.status === 'idle') {
    useMfaStore.setState({ status: 'checking', userId: session.user.id, checkedToken: null });
  }

  const promise = new Promise<MfaStatus>((resolve) => {
    setTimeout(() => {
      void Promise.resolve()
        .then(() => supabase.auth.mfa.getAuthenticatorAssuranceLevel())
        .then(({ data, error }) => {
          if (error || !data) {
            // Fail closed only for someone known to have a factor.
            return hasVerifiedFactor(session) ? 'required' : 'satisfied';
          }
          return data.nextLevel === 'aal2' && data.currentLevel !== 'aal2' ? 'required' : 'satisfied';
        })
        .catch((): MfaStatus => (hasVerifiedFactor(session) ? 'required' : 'satisfied'))
        .then((status: MfaStatus) => {
          if (inflight?.token === token) inflight = null;
          // A newer session (or a sign-out) won the race: leave its result.
          if (latestToken === token) {
            useMfaStore.setState({ status, userId: session.user.id, checkedToken: token });
          }
          resolve(status);
        });
    }, 0);
  });
  inflight = { token, promise };
  return promise;
}

/**
 * The API refused the token with 401 MFA_REQUIRED: the person has a verified
 * factor the browser did not know about (added on another device), or the
 * session fell back to aal1. Not a sign-out: the guards send them to the code
 * step, and the session's own token is remembered so a re-check of that same
 * token keeps the answer instead of reading the stale local factor list.
 */
export function markMfaRequired(session: Pick<Session, 'access_token' | 'user'> | null): void {
  inflight = null;
  latestToken = session?.access_token ?? latestToken;
  useMfaStore.setState((state) => ({
    status: 'required',
    userId: session?.user.id ?? state.userId,
    checkedToken: session?.access_token ?? state.checkedToken,
  }));
}

/**
 * For a sensitive change (password, account deletion): true when the
 * session is at the level its factors call for. When it is not (aal1 with a
 * verified factor), the code step is raised instead and false returned.
 */
export async function ensureMfaSatisfied(): Promise<boolean> {
  try {
    const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (error || !data) return false;
    if (data.nextLevel === 'aal2' && data.currentLevel !== 'aal2') {
      const { data: sessionData } = await supabase.auth.getSession();
      markMfaRequired(sessionData.session);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Resolves with the new access token once the code step is passed (status
 * `satisfied`), or null when the person signs out instead. Used to replay a
 * request the API refused with 401 MFA_REQUIRED.
 */
export function waitForMfaSatisfied(): Promise<string | null> {
  const settle = (state: MfaState): 'go' | 'stop' | null =>
    state.status === 'satisfied' ? 'go' : state.status === 'idle' ? 'stop' : null;
  return new Promise((resolve) => {
    const finish = async (outcome: 'go' | 'stop'): Promise<void> => {
      if (outcome === 'stop') return resolve(null);
      const { data } = await supabase.auth.getSession().catch(() => ({ data: { session: null } }));
      resolve(data.session?.access_token ?? null);
    };
    const now = settle(useMfaStore.getState());
    if (now) {
      void finish(now);
      return;
    }
    const unsubscribe = useMfaStore.subscribe((state) => {
      const outcome = settle(state);
      if (!outcome) return;
      unsubscribe();
      void finish(outcome);
    });
  });
}

/**
 * 403 MFA_ENROLMENT_REQUIRED: a staff account without a verified factor
 * called a console route. SuperAdminGuard then shows the set-up screen even
 * if the session's own factor list said otherwise.
 */
export const useStaffEnrolmentStore = create<{ required: boolean }>(() => ({ required: false }));

export function markStaffEnrolmentRequired(): void {
  useStaffEnrolmentStore.setState({ required: true });
}

export function clearStaffEnrolmentRequired(): void {
  useStaffEnrolmentStore.setState({ required: false });
}

/** Forget the two-factor state (sign-out). */
export function resetMfaState(): void {
  clearStaffEnrolmentRequired();
  inflight = null;
  latestToken = null;
  useMfaStore.setState({ status: 'idle', userId: null, checkedToken: null });
}

export interface VerifyMfaResult {
  error: unknown;
}

/**
 * The code step: the person's verified TOTP factor is challenged and the code
 * verified, which raises the session to aal2 (Supabase emits
 * MFA_CHALLENGE_VERIFIED with the new session).
 */
export async function verifyMfaCode(code: string): Promise<VerifyMfaResult> {
  const { data: factors, error: listError } = await supabase.auth.mfa.listFactors();
  if (listError) return { error: listError };
  const factor = (factors?.totp ?? []).find((f) => f.status === 'verified');
  if (!factor) return { error: { code: 'mfa_factor_not_found' } };
  const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({ factorId: factor.id });
  if (challengeError || !challenge) return { error: challengeError };
  const { error: verifyError } = await supabase.auth.mfa.verify({
    factorId: factor.id,
    challengeId: challenge.id,
    code,
  });
  if (verifyError) return { error: verifyError };
  const { data: sessionData } = await supabase.auth.getSession();
  await checkMfaAssurance(sessionData.session);
  return { error: null };
}
